import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import React, { useState } from 'react';
import { match } from 'ts-pattern';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Caption,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DetailsSidebar,
  DetailsSidebarProvider,
  DetailsSidebarTrigger,
  Divider,
  EluveCardContent,
  EluveCardHeader,
  HStack,
  Icon,
  LinearLogo,
  NewButton,
  Paragraph,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tag,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';
import { timestampToHumanReadable } from '@eluve/date-utils';
import { useSession } from '@eluve/session-helpers';
import {
  EluveAdminOnly,
  OpenInAdminButton,
  OpenInAppButton,
} from '@eluve/smart-blocks';
import { formatAppointmentTagLabel, isLinearIssueLink } from '@eluve/utils';

import { AddAppointmentReviewLinkDialogForm } from './AddAppointmentReviewLinkDialogForm';
import { AppointmentTags } from './AppointmentTags';
import {
  addLowQualityFactorMutation,
  appointmentAnnotationsFragment,
  appointmentLowQualityFactorsFragment,
  appointmentReviewLinksFragment,
  createAppointmentAnnotationMutation,
  removeAppointmentReviewLinkMutation,
  removeLowQualityFactorMutation,
} from './operations';
import { useAppointmentParams } from './useAppointmentParams';
import { useLowQualityFactors } from './useLowQualityFactors';

export const AppointmentAnnotationTools: React.FC<{}> = () => {
  const { tenantId, appointmentId } = useAppointmentParams();
  const [annotation, setAnnotation] = useState('');
  const [factorsOpen, setFactorsOpen] = useState(false);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [toolsPanelOpen, setToolsPanelOpen] = useState(true);
  const email = useSession().email;
  const toast = useToast();

  const { annotations } = useCompleteFragment({
    fragment: appointmentAnnotationsFragment,
    key: {
      id: appointmentId,
    },
  });

  const { low_quality_factors: selectedQualityFactors } = useCompleteFragment({
    fragment: appointmentLowQualityFactorsFragment,
    key: {
      id: appointmentId,
    },
  });

  const { review_links } = useCompleteFragment({
    fragment: appointmentReviewLinksFragment,
    key: {
      id: appointmentId,
    },
  });

  const allLowQualityFactors = useLowQualityFactors();

  const availableFactors = allLowQualityFactors.filter(
    (f) => !selectedQualityFactors.some((lqf) => lqf.factor === f.name),
  );

  const [removeAppointmentReviewLink] = useMutation(
    removeAppointmentReviewLinkMutation,
    {
      onError: () => toast.error('Error removing link.'),
      optimisticResponse: ({ id }) => ({
        deleteAppointmentReviewLinksByPk: {
          __typename: 'AppointmentReviewLinks' as const,
          id,
        },
      }),
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentReviewLinksFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            const removedFactor = data?.deleteAppointmentReviewLinksByPk;
            if (!existing || !removedFactor) {
              return existing;
            }

            return {
              ...existing,
              review_links: existing.review_links.filter(
                (l) => l.id !== removedFactor.id,
              ),
            };
          },
        );
      },
    },
  );

  const [createAppointmentAnnotation] = useMutation(
    createAppointmentAnnotationMutation,
    {
      onError: () => toast.error('Error saving annotation.'),
      optimisticResponse: ({ input }) => ({
        insertAppointmentAnnotationsOne: {
          __typename: 'AppointmentAnnotations' as const,
          id: 'temp-id',
          createdAt: new Date().toISOString(),
          annotation: {
            __typename: 'Annotations' as const,
            id: 'temp-id',
            annotation: input.annotation?.data.annotation ?? '',
          },
        },
      }),
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentAnnotationsFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing || !data?.insertAppointmentAnnotationsOne) {
              return existing;
            }

            const newAnnotation = data.insertAppointmentAnnotationsOne;

            return produce(existing, (draft) => {
              draft.annotations.push({
                ...newAnnotation,
                userEmail: email,
              });
            });
          },
        );
      },
    },
  );

  const [removeLowQualityFactor] = useMutation(removeLowQualityFactorMutation, {
    onError: () => toast.error('Error removing Low Quality Factor.'),
    optimisticResponse: ({ id }) => ({
      deleteAppointmentLowQualityFactorsByPk: {
        __typename: 'AppointmentLowQualityFactors' as const,
        id,
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentLowQualityFactorsFragment,
          key: {
            id: appointmentId,
          },
        },
        (existing) => {
          const removedFactor = data?.deleteAppointmentLowQualityFactorsByPk;
          if (!existing || !removedFactor) {
            return existing;
          }

          return {
            ...existing,
            low_quality_factors: existing.low_quality_factors.filter(
              (lqf) => lqf.id !== removedFactor.id,
            ),
          };
        },
      );
    },
  });

  const [addLowQualityFactor] = useMutation(addLowQualityFactorMutation, {
    onCompleted: () => toast.success('Low Quality Factor added.'),
    onError: () => toast.error('Error adding Low Quality Factor.'),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentLowQualityFactorsFragment,
          key: {
            id: appointmentId,
          },
        },
        (existing) => {
          const newFactor = data?.insertAppointmentLowQualityFactorsOne;
          if (!existing || !newFactor) {
            return existing;
          }

          return {
            ...existing,
            low_quality_factors: [...existing.low_quality_factors, newFactor],
          };
        },
      );
    },
  });

  const handleSaveAnnotation = () => {
    createAppointmentAnnotation({
      variables: {
        input: {
          tenantId,
          appointmentId,
          annotation: {
            data: {
              annotation,
            },
          },
        },
      },
    });
    setAnnotation('');
  };

  const handleAddLowQualityFactor = (factor: string) => {
    addLowQualityFactor({
      variables: {
        input: {
          tenantId,
          appointmentId,
          factor,
        },
      },
    });
  };

  return (
    <DetailsSidebarProvider overlayOnly>
      <DetailsSidebar title="Annotations" size="lg">
        <VStack className="p-8" gap={8}>
          <VStack gap={4}>
            {annotations.map((a) => (
              <Card className="w-full" key={a.id}>
                <EluveCardHeader>
                  <HStack wrap justify="between">
                    <Paragraph>{a.userEmail}</Paragraph>
                    <Paragraph>
                      {timestampToHumanReadable(
                        new Date(a.createdAt).getTime(),
                      )}
                    </Paragraph>
                  </HStack>
                </EluveCardHeader>
                <EluveCardContent>{a.annotation.annotation}</EluveCardContent>
              </Card>
            ))}
          </VStack>
          <VStack>
            <Textarea
              value={annotation}
              onChange={(e) => setAnnotation(e.target.value)}
            />
            <NewButton
              text="Save"
              disabled={!annotation}
              onClick={handleSaveAnnotation}
            />
          </VStack>
        </VStack>
      </DetailsSidebar>
      <Card>
        <Collapsible
          open={toolsPanelOpen}
          onOpenChange={setToolsPanelOpen}
          className="group"
        >
          <EluveCardHeader>
            <HStack justify="between">
              Tools
              <HStack gap={5} wFull={false}>
                <HStack>
                  <EluveAdminOnly>
                    <>
                      <OpenInAdminButton
                        appointmentId={appointmentId}
                        tenantId={tenantId}
                        size="s"
                      />
                      <OpenInAppButton
                        appointmentId={appointmentId}
                        tenantId={tenantId}
                        size="s"
                      />
                    </>
                  </EluveAdminOnly>
                  <DetailsSidebarTrigger>
                    <NewButton
                      icon={{ name: 'Pencil' }}
                      size="s"
                      text={`Annotate (${annotations.length})`}
                      type="outline"
                    />
                  </DetailsSidebarTrigger>
                </HStack>
                <CollapsibleTrigger asChild>
                  <NewButton
                    type="outline"
                    size="s"
                    icon={{
                      name: toolsPanelOpen ? 'ChevronUp' : 'ChevronDown',
                    }}
                  />
                </CollapsibleTrigger>
              </HStack>
            </HStack>
          </EluveCardHeader>
          <CollapsibleContent>
            <EluveCardContent>
              <VStack>
                <HStack gap={4}>
                  <Caption className="whitespace-nowrap" color="tertiary">
                    Low Quality Factors:
                  </Caption>
                  <HStack wrap>
                    {selectedQualityFactors.map((lqf) => (
                      <Tag
                        key={lqf.factor}
                        color="negative"
                        size="s"
                        label={formatAppointmentTagLabel(lqf.factor)}
                        pill
                      >
                        <button
                          onClick={() =>
                            removeLowQualityFactor({
                              variables: {
                                id: lqf.id,
                              },
                            })
                          }
                          className="-m-2 -mr-2.5 size-8 cursor-pointer rounded-full border-8 border-transparent bg-clip-padding p-px hover:bg-bg-negative-strong-secondary hover:text-text-negative-strong"
                        >
                          <Icon name="X" size="2xs" />
                        </button>
                      </Tag>
                    ))}
                    <Popover open={factorsOpen} onOpenChange={setFactorsOpen}>
                      <PopoverTrigger asChild>
                        <NewButton
                          disabled={availableFactors.length === 0}
                          icon={{ name: 'Plus' }}
                          size="s"
                          text="Add"
                          type="outline"
                        />
                      </PopoverTrigger>
                      <PopoverContent
                        side="right"
                        align="start"
                        className="p-0"
                      >
                        <Command>
                          <CommandInput placeholder="Select factor" />
                          <CommandList>
                            <CommandGroup>
                              {availableFactors.map((f) => (
                                <CommandItem
                                  key={f.name}
                                  onSelect={() =>
                                    handleAddLowQualityFactor(f.name)
                                  }
                                >
                                  {formatAppointmentTagLabel(f.name)}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </HStack>
                </HStack>
                <Divider />
                <HStack gap={4}>
                  <Caption color="tertiary">Tags:</Caption>
                  <AppointmentTags
                    tenantId={tenantId}
                    appointmentId={appointmentId}
                  />
                </HStack>
                <Divider />
                <HStack gap={4}>
                  <Caption color="tertiary">Links:</Caption>
                  <HStack wrap>
                    {review_links.map((l) => (
                      <HStack
                        wFull={false}
                        key={l.link}
                        className="rounded-lg border border-border-primary bg-white px-2 text-text-primary hover:border-bg-tertiary"
                      >
                        <a
                          className="flex items-center gap-2 hover:text-text-secondary"
                          href={l.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {match(isLinearIssueLink(l.link))
                            .with(true, () => <LinearLogo className="size-5" />)
                            .otherwise(() => (
                              <Icon name="ExternalLink" size="2xs" />
                            ))}

                          {l.label ?? l.link}
                        </a>
                        <div
                          className="-m-2 -mr-2.5 size-8 cursor-pointer rounded-full border-8 border-transparent bg-clip-padding p-px hover:bg-text-primary hover:text-text-disabled"
                          onClick={() =>
                            removeAppointmentReviewLink({
                              variables: {
                                id: l.id,
                              },
                            })
                          }
                        >
                          <Icon name="X" size="2xs" />
                        </div>
                      </HStack>
                    ))}
                    <NewButton
                      icon={{ name: 'Plus' }}
                      size="s"
                      text="Add"
                      type="outline"
                      onClick={() => setIsAddingLink(true)}
                    />
                    <AddAppointmentReviewLinkDialogForm
                      isDialogOpen={isAddingLink}
                      setIsDialogOpen={setIsAddingLink}
                    />
                  </HStack>
                </HStack>
              </VStack>
            </EluveCardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </DetailsSidebarProvider>
  );
};
